import { useState, useRef, useEffect } from "react";
import { ChevronDownIcon, XMarkIcon, CalendarIcon } from "@heroicons/react/20/solid"; // Added CalendarIcon for the date input
import { DateRange } from "react-date-range";
import { enUS } from "date-fns/locale";
import { format } from "date-fns";

// ... (MultiSelectFilter component code here) ... 

const MultiSelectFilter = ({ label, options, selected, setSelected, selectedIds, setSelectedIds }) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionName, optionId) => {
    if (selected.includes(optionName)) {
      setSelected(selected.filter((item) => item !== optionName));

      const currentIds = selectedIds ? selectedIds.split(",") : [];
      const newIds = currentIds.filter((id) => id !== optionId.toString()).join(",");

      setSelectedIds(newIds);
    } else {
      setSelected([...selected, optionName]);

      const currentIds = selectedIds ? selectedIds.split(",") : [];
      const newIds = [...currentIds, optionId.toString()].join(",");
      setSelectedIds(newIds);
    }
    setOpen(false)
  };

  return (
    <div className="relative w-60" ref={dropdownRef}>
      {/* Fixed Select Box */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white shadow-sm text-sm text-gray-600 hover:border-gray-400 w-full"
      >
        <span className={selected.length > 0 ? "text-gray-600" : "text-gray-400"}>
          {selected.length > 0 ? `${selected.length} selected` : label}
        </span>
        <ChevronDownIcon className="w-4 h-4 text-gray-400 ml-2" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute left-0 mt-2 w-full bg-white border rounded-lg shadow-lg z-10 p-2 max-h-56 overflow-y-auto">
          {options && options.map((option, index) => (
            <label
              key={index}
              className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selected.includes(option.name)}
                onChange={() => toggleOption(option?.name, option?.id)}
                className="h-4 w-4"
              />
              {option.name}
            </label>
          ))}
        </div>
      )}


    </div>
  );
};

const DateRangePickerComponent = ({ range, setRange, startDate, setStartDate, endDate, setEndDate }) => {
  const dropdownRef = useRef(null);
  const [openDatePicker, setOpenDatePicker] = useState(false)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDatePicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (selection) => {
    setRange([selection]);
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
    // if (startDate && endDate) {
    //   setOpenDatePicker(false)
    // }
  };

  const handleClear = (e) => {
    e.stopPropagation(); // prevent reopening picker
    setStartDate(null);
    setEndDate(null);
    setRange([
      {
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
      },
    ]);
  };
  return (
    <div className="relative w-60">
      <button
        type="button"
        className="flex items-center justify-between border rounded-lg px-3 py-2 bg-white shadow-sm text-sm text-gray-600 hover:border-gray-400 w-full"
        onClick={() => setOpenDatePicker(true)}
      >
        <span className={(startDate && endDate) ? "text-gray-600" : "text-gray-400"}>
          {(startDate && endDate) ?
            <>
              {format(startDate, "dd-MM-yy")} - {format(endDate, "dd-MM-yy")}
            </>
            :
            'Date Range'
          }
        </span>
        {(startDate && endDate) &&
          <XMarkIcon className="w-4 h-4 text-gray-400 ml-2" onClick={(e) => handleClear(e)} />
        }
      </button>
      {openDatePicker &&
        <div className="absolute z-40" ref={dropdownRef}>
          <DateRange
            onChange={(item) => handleSelect(item.selection)}
            ranges={range}
            locale={enUS}
            showDateDisplay={true}
            className="shadow"
          />
        </div>
      }
    </div>
  )
}


// -----------------------------------------------------------------------

export default function Filters({ filterConfigs, selectedFilters, setSelectedFilters, selectedFiltersIds, setSelectedFiltersIds, date, dateRangePicker, startDate, setStartDate, endDate, setEndDate }) {
  const [range, setRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const setFilter = (key, value) => {
    setSelectedFilters((prev) => ({ ...prev, [key]: value }));
  };
  const setFilterIds = (key, value) => {
    setSelectedFiltersIds((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    // Clear dynamic filters
    const clearedNames = {};
    const clearedIds = {};
    if (filterConfigs) {
      filterConfigs.forEach((f) => {
        clearedNames[f.key] = [];
        clearedIds[f.key] = null;
      });
      setSelectedFilters(clearedNames);
      setSelectedFiltersIds(clearedIds);
    }

    // Clear date filters if present
    if (date) {
      date.setFromDate(null);
      date.setToDate(null);
    }
    if (dateRangePicker == 'true') {
      setStartDate(null);
      setEndDate(null);
      setRange([
        {
          startDate: new Date(),
          endDate: new Date(),
          key: "selection",
        },
      ]);
    }
  };

  // Custom Date Input Component
  const DateInput = ({ label, value, setValue }) => (
    <div className="flex items-center border rounded-lg bg-white shadow-sm text-sm text-gray-600 hover:border-gray-400 w-44">
      {label && <span className="pl-3 py-2 text-gray-400">{label}</span>}
      <input
        type="date"
        value={value || ''}
        onChange={(e) => setValue(e.target.value)}
        // Using a standard input-date style that tries to hide the native calendar icon for custom styling
        className={`flex-grow bg-transparent px-3 py-2 text-gray-600 focus:outline-none ${!label ? 'pl-3' : 'pl-1'}`}
      />
      <CalendarIcon className="w-4 h-4 text-gray-400 mr-3" />
    </div>
  );

  // Check if any filters (config or date) are present to decide if the clear button should be shown
  const hasFilters = (filterConfigs && filterConfigs.length > 0) || date;


  return (
    <div className="flex flex-col gap-3">
      {/* Filter Inputs Row */}
      <div className="flex items-center gap-3 flex-wrap">

        {/* MultiSelect Filters - Only render if filterConfigs is present */}
        {filterConfigs && filterConfigs.map((filter) => (
          <MultiSelectFilter
            key={filter.key}
            label={filter.label}
            options={filter.options}
            selected={selectedFilters[filter.key]}
            setSelected={(value) => setFilter(filter.key, value)}
            selectedIds={selectedFiltersIds[filter.key]}
            setSelectedIds={(value) => setFilterIds(filter.key, value)}
          />
        ))}

        {/* Date Range Inputs - Shown only if 'date' prop is provided */}
        {date && (
          <>
            {/* From Date Input */}
            <span className="text-gray-500 text-sm">Last login</span>

            <DateInput
              // label="Last Login: "
              value={date.fromDate}
              setValue={date.setFromDate}
            />

            {/* Separator (e.g., 'to') */}
            <span className="text-gray-500 text-sm">to</span>

            {/* To Date Input */}
            <DateInput
              label={null} // Pass null to hide the label on the 'to' date field
              value={date.toDate}
              setValue={date.setToDate}
            />
          </>
        )}

        {dateRangePicker == 'true' &&
          <DateRangePickerComponent range={range} setRange={setRange} startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} />
        }


        {/* Clear Filters - Shown if EITHER dynamic filters OR date filters are present */}
        {hasFilters &&
          <button
            onClick={clearFilters}
            className="flex items-center gap-1 text-primary-600 text-sm hover:underline ml-2"
          >
            <span>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M22.122 13.465L23.536 14.879L21.415 17L23.536 19.122L22.122 20.536L20 18.414L17.88 20.536L16.465 19.121L18.586 17.001L16.465 14.879L17.879 13.465L20 15.586L22.122 13.465ZM3 3H21L14 12.817V21H10V12.817L3 3Z"
                  fill="#446DB5"
                />
              </svg>
            </span>
            Clear filter
          </button>
        }
      </div>

      {/* Selected Tags Row (Only shows tags for filterConfigs) */}
      <div className="flex flex-wrap gap-2">
        {filterConfigs && filterConfigs.map((filter) =>
          selectedFilters && selectedFilters[filter.key].map((option, idx) => (
            <span
              key={option}
              className={`flex items-center gap-1 bg-${filter.color}-100 text-${filter.color}-700 px-2 py-0.5 rounded-full text-xs`}
            >
              {option}
              <XMarkIcon
                className="w-3 h-3 cursor-pointer"
                onClick={() => {
                  setFilter(filter.key, selectedFilters[filter.key].filter((o) => o !== option))

                  // Remove corresponding ID from comma-separated selectedFiltersIds
                  const currentIdsArray = selectedFiltersIds[filter.key]
                    ? selectedFiltersIds[filter.key].split(",")
                    : [];

                  // Use index to remove the correct ID
                  const newSelectedIdsArray = currentIdsArray.filter(
                    (_, i) => i !== idx
                  );

                  const newSelectedIds = newSelectedIdsArray.join(",");
                  setSelectedFiltersIds((prev) => ({
                    ...prev,
                    [filter.key]: newSelectedIds,
                  }));
                }}
              />
            </span>
          ))
        )}
      </div>
    </div>
  );
}